/**
 * Follow service layer.
 * Abstracts all Firestore operations for the follow/unfollow system.
 * Uses subcollections: users/{userId}/following and users/{userId}/followers.
 * All follow/unfollow operations use batch writes for atomicity.
 */
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  serverTimestamp,
  writeBatch,
  increment,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const USERS_COLLECTION = 'users';
const FOLLOWING_SUBCOLLECTION = 'following';
const FOLLOWERS_SUBCOLLECTION = 'followers';

/**
 * Follow a user. Batch write that:
 * 1. Adds targetUserId to currentUser's following subcollection
 * 2. Adds currentUserId to targetUser's followers subcollection
 * 3. Increments currentUser's followingCount
 * 4. Increments targetUser's followersCount
 *
 * @param {string} currentUserId - The user who is following.
 * @param {string} targetUserId - The user being followed.
 * @returns {Promise<void>}
 */
export const followUser = async (currentUserId, targetUserId) => {
  try {
    if (currentUserId === targetUserId) {
      throw new Error('You cannot follow yourself.');
    }

    const batch = writeBatch(db);

    // Add to currentUser's following subcollection
    const followingRef = doc(
      db,
      USERS_COLLECTION,
      currentUserId,
      FOLLOWING_SUBCOLLECTION,
      targetUserId
    );
    batch.set(followingRef, { createdAt: serverTimestamp() });

    // Add to targetUser's followers subcollection
    const followersRef = doc(
      db,
      USERS_COLLECTION,
      targetUserId,
      FOLLOWERS_SUBCOLLECTION,
      currentUserId
    );
    batch.set(followersRef, { createdAt: serverTimestamp() });

    // Increment currentUser's followingCount
    const currentUserRef = doc(db, USERS_COLLECTION, currentUserId);
    batch.update(currentUserRef, { followingCount: increment(1) });

    // Increment targetUser's followersCount
    const targetUserRef = doc(db, USERS_COLLECTION, targetUserId);
    batch.update(targetUserRef, { followersCount: increment(1) });

    await batch.commit();
  } catch (error) {
    handleServiceError(error, 'Follow user', error.message || 'Failed to follow user. Please try again.');
  }
};

/**
 * Unfollow a user. Batch write that:
 * 1. Removes targetUserId from currentUser's following subcollection
 * 2. Removes currentUserId from targetUser's followers subcollection
 * 3. Decrements currentUser's followingCount
 * 4. Decrements targetUser's followersCount
 *
 * @param {string} currentUserId - The user who is unfollowing.
 * @param {string} targetUserId - The user being unfollowed.
 * @returns {Promise<void>}
 */
export const unfollowUser = async (currentUserId, targetUserId) => {
  try {
    const batch = writeBatch(db);

    // Remove from currentUser's following subcollection
    const followingRef = doc(
      db,
      USERS_COLLECTION,
      currentUserId,
      FOLLOWING_SUBCOLLECTION,
      targetUserId
    );
    batch.delete(followingRef);

    // Remove from targetUser's followers subcollection
    const followersRef = doc(
      db,
      USERS_COLLECTION,
      targetUserId,
      FOLLOWERS_SUBCOLLECTION,
      currentUserId
    );
    batch.delete(followersRef);

    // Decrement currentUser's followingCount
    const currentUserRef = doc(db, USERS_COLLECTION, currentUserId);
    batch.update(currentUserRef, { followingCount: increment(-1) });

    // Decrement targetUser's followersCount
    const targetUserRef = doc(db, USERS_COLLECTION, targetUserId);
    batch.update(targetUserRef, { followersCount: increment(-1) });

    await batch.commit();
  } catch (error) {
    handleServiceError(error, 'Unfollow user', 'Failed to unfollow user. Please try again.');
  }
};

/**
 * Check if currentUser is following targetUser.
 * @param {string} currentUserId
 * @param {string} targetUserId
 * @returns {Promise<boolean>}
 */
export const checkIfFollowing = async (currentUserId, targetUserId) => {
  try {
    const followingRef = doc(
      db,
      USERS_COLLECTION,
      currentUserId,
      FOLLOWING_SUBCOLLECTION,
      targetUserId
    );
    const docSnap = await getDoc(followingRef);
    return docSnap.exists();
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Check if following]', error);
    }
    return false;
  }
};

/**
 * Get paginated list of followers for a user.
 * @param {string} userId - The user whose followers to retrieve.
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc - Pagination cursor.
 * @param {number} [pageLimit=20] - Max number of results.
 * @returns {Promise<{followers: Array<Object>, lastDoc: import('firebase/firestore').DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getFollowers = async (userId, lastDoc = null, pageLimit = 20) => {
  try {
    const followersRef = collection(
      db,
      USERS_COLLECTION,
      userId,
      FOLLOWERS_SUBCOLLECTION
    );
    let q;

    if (lastDoc) {
      q = query(
        followersRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        firestoreLimit(pageLimit)
      );
    } else {
      q = query(
        followersRef,
        orderBy('createdAt', 'desc'),
        firestoreLimit(pageLimit)
      );
    }

    const querySnapshot = await getDocs(q);
    const followerIds = querySnapshot.docs.map((docSnap) => ({
      userId: docSnap.id,
      createdAt:
        docSnap.data().createdAt instanceof Timestamp
          ? docSnap.data().createdAt.toDate().toISOString()
          : new Date().toISOString(),
      _docSnapshot: docSnap,
    }));

    // Fetch full profile for each follower
    const followers = await Promise.all(
      followerIds.map(async (follower) => {
        const userRef = doc(db, USERS_COLLECTION, follower.userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          return {
            id: userSnap.id,
            ...userSnap.data(),
            followedAt: follower.createdAt,
            _docSnapshot: follower._docSnapshot,
          };
        }
        return null;
      })
    );

    const validFollowers = followers.filter(Boolean);
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    const hasMore = querySnapshot.docs.length === pageLimit;

    return { followers: validFollowers, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get followers', 'Failed to load followers. Please try again.');
  }
};

/**
 * Get paginated list of users that a user is following.
 * @param {string} userId - The user whose following list to retrieve.
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc - Pagination cursor.
 * @param {number} [pageLimit=20] - Max number of results.
 * @returns {Promise<{following: Array<Object>, lastDoc: import('firebase/firestore').DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getFollowing = async (userId, lastDoc = null, pageLimit = 20) => {
  try {
    const followingRef = collection(
      db,
      USERS_COLLECTION,
      userId,
      FOLLOWING_SUBCOLLECTION
    );
    let q;

    if (lastDoc) {
      q = query(
        followingRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        firestoreLimit(pageLimit)
      );
    } else {
      q = query(
        followingRef,
        orderBy('createdAt', 'desc'),
        firestoreLimit(pageLimit)
      );
    }

    const querySnapshot = await getDocs(q);
    const followingIds = querySnapshot.docs.map((docSnap) => ({
      userId: docSnap.id,
      createdAt:
        docSnap.data().createdAt instanceof Timestamp
          ? docSnap.data().createdAt.toDate().toISOString()
          : new Date().toISOString(),
      _docSnapshot: docSnap,
    }));

    // Fetch full profile for each followed user
    const following = await Promise.all(
      followingIds.map(async (followed) => {
        const userRef = doc(db, USERS_COLLECTION, followed.userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          return {
            id: userSnap.id,
            ...userSnap.data(),
            followedAt: followed.createdAt,
            _docSnapshot: followed._docSnapshot,
          };
        }
        return null;
      })
    );

    const validFollowing = following.filter(Boolean);
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    const hasMore = querySnapshot.docs.length === pageLimit;

    return { following: validFollowing, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get following', 'Failed to load following list. Please try again.');
  }
};

/**
 * Get the list of user IDs that the current user is following.
 * Used for building the personalized feed.
 * @param {string} userId
 * @returns {Promise<string[]>} Array of user IDs being followed.
 */
export const getFollowingIds = async (userId) => {
  try {
    const followingRef = collection(
      db,
      USERS_COLLECTION,
      userId,
      FOLLOWING_SUBCOLLECTION
    );
    const querySnapshot = await getDocs(followingRef);
    return querySnapshot.docs.map((docSnap) => docSnap.id);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Get following IDs]', error);
    }
    return [];
  }
};

/**
 * Get suggested users for the current user to follow.
 * Returns users that the current user is not already following.
 * @param {string} currentUserId - The current user's ID.
 * @param {number} [resultLimit=5] - Max number of suggestions.
 * @returns {Promise<Array<Object>>} Array of user profile objects.
 */
export const getSuggestedUsers = async (currentUserId, resultLimit = 5) => {
  try {
    // Get list of users the current user is already following
    const followingIds = await getFollowingIds(currentUserId);
    const excludeIds = [currentUserId, ...followingIds];

    // Query all users and filter out those already followed
    // Firestore doesn't support "not in" for large arrays, so we fetch more and filter
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(
      usersRef,
      orderBy('createdAt', 'desc'),
      firestoreLimit(resultLimit + excludeIds.length + 10)
    );

    const querySnapshot = await getDocs(q);
    const suggestions = [];

    for (const docSnap of querySnapshot.docs) {
      if (suggestions.length >= resultLimit) break;
      if (!excludeIds.includes(docSnap.id)) {
        suggestions.push({ id: docSnap.id, ...docSnap.data() });
      }
    }

    return suggestions;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Get suggested users]', error);
    }
    return [];
  }
};
